import { LitElement, html, css, nothing, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { nsDirectoryServiceContext } from '../ns-services/contexts.js';

import { ButtressDbService, buttressDbServiceContext } from '@buttress/crag';
import '@awesome.me/webawesome/dist/components/button/button.js';

import { NSDirectoryService } from '../ns-services/ns-directory-service.js';
import { viewPath } from '../helpers/route.js';

import { nsFlyoutStyles, nsSharedStyles, nsLayoutStyles, nsButtonStyles } from '../ns-styles/index.js';

import type Notification from '../models/notification.js';
import '../ns-elements/ns-empty-state.js';
import './ns-notification-row.js';
import { ButtressSubscriptions } from '../helpers/buttress-subscriptions.js';
import { markNotificationsRead, notificationPath } from '../helpers/notifications.js';

const NOTIFICATIONS_QUERY_LIMIT = 20;

@customElement(NSNotificationDropdown.is)
export class NSNotificationDropdown extends LitElement {
  static get is() {
    return 'ns-notification-dropdown';
  }

  @consume({ context: buttressDbServiceContext })
  private __db?: ButtressDbService;

  @consume({ context: nsDirectoryServiceContext })
  private __directoryService?: NSDirectoryService;

  private readonly __subscriptions = new ButtressSubscriptions(this, () => this.__db);

  @property({ type: Boolean, reflect: true })
  open = false;

  // Only unread ones are listed: reading one, by opening or ticking it, takes it off; the notifications page keeps history
  @state() private __notifications: Notification[] = [];

  // Nothing is said about an empty list until the first load has answered
  @state() private __loaded = false;

  static styles = [
    nsLayoutStyles,
    nsSharedStyles,
    nsFlyoutStyles,
    nsButtonStyles,
    css`
      :host {
        background: var(--wa-color-surface-lowered);
        color: var(--wa-color-text-normal);
        display: flex;
        flex-direction: column;
        /* Deliberately no fixed/100% height — this panel shrinks to its content and only grows
           up to the max-height the host page sets (ns-app.ts), instead of always reaching that
           cap and leaving an empty void under a short list. */
      }

      .heading {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--wa-space-xs);
        padding: var(--wa-space-s) var(--wa-space-m);
        border-bottom: 1px solid var(--wa-color-text-normal);
      }

      .heading h2 {
        margin: 0;
        font-size: var(--wa-font-size-m);
        font-weight: 600;
        opacity: 0.9;
      }

      .notifications-list {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;

        display: flex;
        flex-direction: column;
        gap: var(--wa-space-xs);
        padding: var(--wa-space-s);
      }

      .footer {
        flex-shrink: 0;
        display: flex;
        justify-content: space-between;
        padding: var(--wa-space-2xs) var(--wa-space-m) var(--wa-space-s);
      }

      wa-button {
        --ns-button-min-width: auto;

        font-size: var(--wa-font-size-xs);
      }

      .empty-state {
        flex: 1 1 auto;
      }
    `,
  ];

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this.__db) throw new Error('Unable to find db service');

    if (!this.__directoryService) throw new Error('Unable to find directory service');
  }

  updated(changedProperties: PropertyValues<this>) {
    super.updated(changedProperties);

    if (!changedProperties.has('open')) return;

    if (this.open) {
      this.__subscribe();
    } else {
      this.__unsubscribe();
    }
  }

  private __subscribe() {
    if (!this.__db || this.__subscriptions.active) return;

    this.__subscriptions.subscribe('notification.*', () => this.__load());
    this.__load();
  }

  private __unsubscribe() {
    this.__subscriptions.clear();
  }

  private async __load(): Promise<void> {
    if (!this.__db) return;

    await this.__directoryService?.initialised;
    const myPersonId = this.__directoryService?.me?.id;
    if (!myPersonId) return;

    const result = await this.__db.query(
      'notification',
      { personId: { $eq: myPersonId }, readAt: { $eq: null } },
      { limit: NOTIFICATIONS_QUERY_LIMIT, sort: { path: 'createdAt', type: 'DATE', direction: 'DESC' } },
    );
    this.__notifications = (result.results ?? []) as unknown as Notification[];
    this.__loaded = true;
  }

  private __close() {
    this.dispatchEvent(new CustomEvent('close-start-menu', { bubbles: true, composed: true }));
  }

  private __open(ev: CustomEvent<{ notification: Notification }>) {
    const { notification } = ev.detail;
    markNotificationsRead(this.__db, [notification]);
    const path = notificationPath(notification);
    if (path) viewPath(path);
    this.__close();
  }

  private __seeAll() {
    viewPath('/notifications');
    this.__close();
  }

  private __settings() {
    viewPath('/my-account/notifications');
    this.__close();
  }

  render() {
    return html`
      <div class="heading">
        <h2>Notifications</h2>
        ${this.__notifications.length > 0
          ? html`
              <wa-button
                appearance="plain"
                size="small"
                @click=${() => markNotificationsRead(this.__db, this.__notifications)}
              >
                Mark all as read
              </wa-button>
            `
          : nothing}
      </div>
      ${!this.__loaded
        ? nothing
        : this.__notifications.length === 0
          ? html`
              <ns-empty-state class="empty-state" message="No new notifications."></ns-empty-state>
            `
          : html`
              <div
                class="notifications-list"
                @notification-open=${this.__open}
                @notification-read=${(ev: CustomEvent<{ notification: Notification }>) =>
                  markNotificationsRead(this.__db, [ev.detail.notification])}
              >
                ${this.__notifications.map(
                  (notification) => html`
                    <ns-notification-row .notification=${notification}></ns-notification-row>
                  `,
                )}
              </div>
            `}
      <div class="footer">
        <wa-button appearance="plain" size="small" @click=${this.__seeAll}>See all notifications</wa-button>
        <wa-button appearance="plain" size="small" @click=${this.__settings}>Notification settings</wa-button>
      </div>
    `;
  }
}
